import { useState } from 'react';
import { App } from 'antd';
import { Order } from '../types/orders';
import { KitchenOrderDto, KitchenOrderStatus, KdsOrderItemDto } from '../types/kitchen';
import {
  useUpdateKitchenOrderItemsStatusMutation,
  useComandearOrderItemsMutation,
} from '../services/kitchenApi';
import { useHasProductionCentersQuery } from '../services/productionCenterApi';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { printAgentService } from '../services/printAgentService';
import { bluetoothPrintService } from '../services/bluetoothPrintService';
import { useCloudPrint } from './useCloudPrint';

// "Uncategorized" items (no production centers) are allowed to show/print by default,
// matching the behavior of the initial comandear flow.
const canShowOnKds = (item: KdsOrderItemDto): boolean => {
  if (!item.productionCenters || item.productionCenters.length === 0) return true;
  return item.productionCenters.some(pc => pc.showOnScreen);
};

const canPrint = (item: KdsOrderItemDto): boolean => {
  if (!item.productionCenters || item.productionCenters.length === 0) return true;
  return item.productionCenters.some(pc => pc.printTicket);
};

/**
 * Encapsulates the "resend kitchen order" flow:
 * - reactivates selected kitchen order items back to Pending (so they reappear on KDS)
 * - reprints the comanda via Print Agent or Bluetooth (if enabled)
 *
 * The caller is responsible for rendering <ResendOrderModal> and wiring up
 * `resendModalVisible`, `openResendModal`, and `handleResendConfirm`.
 */
export const useResendKitchenOrder = (
  order: Order | undefined | null,
  tableLabel: string
) => {
  const { message } = App.useApp();
  const [resendModalVisible, setResendModalVisible] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  const cloudPrintEnabled = cloudPrintSetting?.value ?? false;
  const { printKitchenOrder: cloudPrintKitchenOrder } = useCloudPrint();

  const { data: hasProductionCentersData } = useHasProductionCentersQuery();
  const { data: kdsEnabledSetting } = useGetBoolSettingQuery('KitchenDisplayEnabled');
  const { data: printComandasSetting } = useGetBoolSettingQuery('ImprimirComandas');
  const hasProductionCenters = hasProductionCentersData?.hasProductionCenters ?? false;
  const shouldSendToKds = hasProductionCenters || (kdsEnabledSetting?.value ?? false);
  const shouldPrintComandas = printComandasSetting?.value ?? true;

  const [updateKitchenOrderItemsStatus] = useUpdateKitchenOrderItemsStatusMutation();
  const [comandearOrderItems] = useComandearOrderItemsMutation();

  const openResendModal = () => {
    if (!order) {
      message.error('No hay orden activa para reenviar');
      return;
    }
    setResendModalVisible(true);
  };

  const closeResendModal = () => setResendModalVisible(false);

  const handleResendConfirm = async (
    selectedKitchenOrders: KitchenOrderDto[],
    selectedItemIds: Set<string>,
    selectedMissingItemIds: Set<string> = new Set()
  ) => {
    if (!order) return;
    setResendModalVisible(false);
    setPrinting(true);

    try {
      // Renglones que nunca tuvieron comanda: aquí no hay nada que reactivar, hay que
      // crearla. El backend la arma leyendo lo persistido y es idempotente, así que
      // reintentar tras un error no duplica.
      //
      // Los items que devuelve entran a la misma cola de impresión que el resto: sin eso,
      // en una cocina que trabaja con papel el rescate llegaría al KDS y no saldría ticket.
      let rescuedItems: KdsOrderItemDto[] = [];

      if (selectedMissingItemIds.size > 0) {
        try {
          const created = await comandearOrderItems({
            orderId: order.id,
            orderItemIds: Array.from(selectedMissingItemIds),
          }).unwrap();

          if (created.orders.length > 0) {
            rescuedItems = created.orders.flatMap(ko => ko.items.filter(i => !i.isCancelled));
            message.success(
              `${selectedMissingItemIds.size} producto(s) sin comanda enviado(s) a cocina`,
            );
          } else {
            // Ni error ni envío: el gate del KDS está apagado, o alguien más ya los mandó.
            message.info(
              'Los productos sin comanda no se enviaron al KDS (revisa si el envío a cocina está habilitado).',
              8,
            );
          }
        } catch (err) {
          console.error('Failed to comandear missing order items:', err);
          message.error(
            'No se pudo enviar a cocina los productos sin comanda. Vuelve a intentarlo o avisa a cocina directamente.',
            8,
          );
        }
      }

      // Collect all selected (non-cancelled) raw items across kitchen orders.
      // Keep them as KdsOrderItemDto so we can check productionCenters for each.
      const selectedItems = selectedKitchenOrders.flatMap(ko =>
        ko.items.filter(i => !i.isCancelled && selectedItemIds.has(i.id))
      );

      // Split selected items by what each is allowed to do based on production center settings.
      const idsToReactivate = new Set(
        selectedItems.filter(canShowOnKds).map(i => i.id)
      );
      // Lo rescatado se imprime, pero NO se reactiva: acaba de nacer en Pending.
      const itemsToPrint = [...selectedItems, ...rescuedItems].filter(canPrint);

      // Reactivate items to Pending on KDS — only those whose production center has
      // ShowOnScreen = true (or no production centers at all).
      // Se cuenta lo que REALMENTE se reactivó, no lo que se intentó. El toast de abajo
      // usaba idsToReactivate.size, así que un PATCH que devolvía 409 o 500 se anunciaba
      // igual como "N producto(s) reactivado(s) en cocina": el mesero se iba tranquilo y la
      // cocina nunca veía nada. Es el ticket de soporte "reenvié la comanda y no llegó".
      let reactivatedCount = 0;
      let reactivationFailed = false;

      if (shouldSendToKds && idsToReactivate.size > 0) {
        for (const ko of selectedKitchenOrders) {
          const itemIdsToReactivate = ko.items
            .filter(i => idsToReactivate.has(i.id))
            .map(i => i.id);
          if (itemIdsToReactivate.length > 0) {
            try {
              await updateKitchenOrderItemsStatus({
                id: ko.id,
                kitchenOrderItemIds: itemIdsToReactivate,
                status: KitchenOrderStatus.Pending,
              }).unwrap();
              reactivatedCount += itemIdsToReactivate.length;
            } catch (err) {
              console.warn('Failed to reactivate kitchen order items:', ko.id, err);
              reactivationFailed = true;
            }
          }
        }
      }

      // Group printable items by (productName | variants | notes) to merge duplicates.
      const groupMap = new Map<string, { orderItemId?: string; productName: string; quantity: number; variants?: string; notes?: string }>();
      for (const item of itemsToPrint) {
        const key = `${item.productName}|${item.variants || ''}|${item.notes || ''}`;
        const existing = groupMap.get(key);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          groupMap.set(key, {
            orderItemId: item.orderItemId || undefined,
            productName: item.productName,
            quantity: item.quantity,
            variants: item.variants || undefined,
            notes: item.notes || undefined,
          });
        }
      }
      const printableGrouped = Array.from(groupMap.values());

      if (shouldPrintComandas && printableGrouped.length > 0) {
        const printItems = printableGrouped.map(item => ({
          id: '',
          orderId: order.id,
          productId: '',
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: 0,
          total: 0,
          variants: item.variants
            ? [{ id: '', orderItemId: '', variantId: '', variantName: item.variants, priceModifier: 0 }]
            : [],
          notes: item.notes,
          isCancelled: false,
        }));

        if (cloudPrintEnabled) {
          await cloudPrintKitchenOrder({
            orderId: order.id,
            tableLabel,
            waiterName: order.assignedWaiterName || '',
            items: printItems.map(item => ({
              productId: item.productId,
              productName: item.productName,
              quantity: item.quantity,
              variants: item.variants
                ? item.variants.map((v: any) => v.variantName || v)
                : [],
              notes: item.notes,
            })),
          });
          message.success(`${printItems.length} producto(s) reimpreso(s)`);
        } else {
        const printAgentAvailable = await printAgentService.isAvailable();
        const bluetoothAvailable = bluetoothPrintService.isAvailable();

        if (printAgentAvailable) {
          const result = await printAgentService.printKitchenOrder(
            order, tableLabel, order.assignedWaiterName || '', printItems as any
          );
          if (result.success) {
            message.success(`${printItems.length} producto(s) reimpreso(s)`);
          } else {
            message.error(`Error al reimprimir: ${result.message}`);
          }
        } else if (bluetoothAvailable) {
          const result = await bluetoothPrintService.printKitchenOrder(
            order, tableLabel, order.assignedWaiterName || '', printItems as any
          );
          if (result.success) {
            message.success(`${printItems.length} producto(s) reimpreso(s)`);
          } else {
            message.error(`Error al reimprimir: ${result.message}`);
          }
        } else {
          message.warning('No hay servicio de impresión disponible');
        }
        }
      } else if (idsToReactivate.size > 0 && printableGrouped.length === 0) {
        if (reactivationFailed && reactivatedCount === 0) {
          message.error('No se pudo reactivar la comanda en cocina. Vuelve a intentarlo o avisa a cocina directamente.', 8);
        } else if (reactivationFailed) {
          message.warning(
            `Solo ${reactivatedCount} de ${idsToReactivate.size} producto(s) se reactivaron en cocina. Revisa el resto antes de irte.`,
            8,
          );
        } else {
          message.success(`${reactivatedCount} producto(s) reactivado(s) en cocina`);
        }
      } else if (idsToReactivate.size === 0 && printableGrouped.length === 0 && selectedMissingItemIds.size === 0) {
        // Nothing happened. Distinguish between "global toggles off" and "items don't qualify".
        // (Si se mandaron renglones sin comanda ya hubo un toast arriba, no hace falta este.)
        if (!shouldSendToKds && !shouldPrintComandas) {
          message.info('El envío a cocina y la impresión de comandas están deshabilitados en la configuración.');
        } else if (selectedItems.length === 0) {
          message.info('No seleccionaste ningún producto para reenviar.');
        } else {
          message.info('Ningún producto seleccionado aplica para reenviar (revisa los centros de producción del producto).');
        }
      }
    } catch (error: any) {
      console.error('Error resending order:', error);
      message.error('Error al reenviar la comanda');
    } finally {
      setPrinting(false);
    }
  };

  return {
    resendModalVisible,
    openResendModal,
    closeResendModal,
    handleResendConfirm,
    printing,
  };
};
